import React, { useEffect } from 'react';
import { useRouteMatch, Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown, { DropdownTrigger, DropdownContent } from 'react-simple-dropdown';
import moment from 'moment';

import { CustomDropdown, DeactivateDropdown } from '../../utilites';
import Loader from './Loader/Loader';
import {permissionsAccess, calculateAge} from '../../../helpers/common';
import { kbStatus } from '../../../helpers/data-constants';
import {
  changeClientShortInfo,
  clientShortInfoSaga,
  resetShortInfo,
  errorAccessToContractCreation
} from '../../../redux/actions/clientShortInfoAction';
import { printClientInfo } from '../../../redux/actions/clientsAction';

import '../../../assets/styles/components/_dropdown.scss';

import PlusSmallIcon from '../../../assets/icons/plus-small-2.svg';
import PowerOffIcon from '../../../assets/icons/power-off.svg';
import PrintIcon from '../../../assets/icons/print.svg';
import CaretDownIcon from '../../../assets/icons/caret-down.svg';
import useClientValidation from '../useClientValidation.hook';

const ClientShortInfo = ({ activeInfoTab }) => {
  const dispatch = useDispatch();
  const { client, isLoading, cashBoxes } = useSelector(({ clientInfo }) => clientInfo);
  const { accessFeatures } = useSelector(({ user }) => user);
  
  const match = useRouteMatch();
  const history = useHistory();
  const clientId = match.params.id;
  const pageCreateContract =
  history.location.pathname.split(match.url)[1] === '/contract' ? null : true;
  const inactiveClient = !client.document || client?.document?.active === false
  const { validateClientStatus, clientIdNumber } = useClientValidation({client, inactiveClient});

  useEffect(() => {
    return () =>  {
      dispatch(errorAccessToContractCreation('')) 
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(clientShortInfoSaga(clientId));
    return () => {
      dispatch(resetShortInfo());
    };
  }, [dispatch, clientId]);

  const changeStatus = (rejectionReason) => {
    dispatch(
      changeClientShortInfo(clientId, {
        clientRequestInfoBean: {
          active: !client.active,
          rejectionReason,
        },
      }),
    );
  };

  let cashBoxNames = '';
  if (cashBoxes) {
    cashBoxNames = cashBoxes.map((cashBox) => cashBox.name).join(', ');
  }

  let docName = '';
  if (client.document) {
    let { country, documentType } = client.document;
    docName += documentType === 'PASSPORT' ? 'Паспорт' : documentType;
    docName += country.toLowerCase() === 'беларусь' ? ' РБ' : '  другого государства';
  }

  const isClientInactive = () => {
    validateClientStatus()
  };

  const InfoItem = ({ description, value, className = "" }) => (
    <><p className={`client-info-bottom-item-description ${className}`}>{description}</p>
      <p className="client-info-bottom-item-value nowrap">{value}</p></>
  );

  const getStatusKey = (statusText) => {
    return Object.keys(kbStatus).find(key => kbStatus[key].text === statusText);
  };

  const statusKey = getStatusKey(client.kbStatus);

  const status = kbStatus[statusKey];

  return (
    <>
      {isLoading ? (
        <Loader pageCreateContract={pageCreateContract} />
      ) : (
        <>
          {client && (
            <div className="client-info">
              <div className="client-info-top">
                <div className="client-info-top-left client-info-top">
                  <div className="client-info-top-title">
                    <h2>
                      {client.lastName} {client.firstName} {client.middleName}
                    </h2>
                    {accessFeatures['client.age'] && client.dateOfBirth && (
                      <div className="client-info-top-age"><span className="date">{moment(client.dateOfBirth).format('DD.MM.YYYY')} </span>
                      <span>({calculateAge(client.dateOfBirth)})</span></div>)}
                  </div>
                </div>
                <div className="client-info-top-right">
                  {client?.active && pageCreateContract && permissionsAccess("action.deal.create") && (
                    <div onClick = {isClientInactive}>
                      <Link
                        href="#"
                        className={ (inactiveClient || clientIdNumber)
                          ? "disabled-action-item disabled-action-item__button" 
                          : "button-icontrigger-accent caption hover" 
                        }
                        to={ inactiveClient || clientIdNumber ? "#" : `${match.url}/contract` }>
                        <span className="icon">
                          <PlusSmallIcon />
                        </span>
                        <span>Создать договор</span>
                      </Link>
                    </div>
                  )}
                  <div>
                    {permissionsAccess("action.client.update.activation") ? (
                      client.active ? (
                        <DeactivateDropdown
                          triggerText={'Деактивировать'}
                          headText={`Вы действительно хотите деактивировать клиента?`}
                          onAction={changeStatus}
                          defaultInput={client.rejectionReason}
                          maxLength={100}
                          buttonText={'Деактивировать'}>
                          <PowerOffIcon />
                        </DeactivateDropdown>
                      ) : (
                        <CustomDropdown
                          triggerText={'Активировать'}
                          headText={`Вы действительно хотите активировать клиента?`}
                          onAction={changeStatus}
                          buttonText={'Активировать'}>
                          <PowerOffIcon />
                        </CustomDropdown>
                      )
                    ) : null}
                  </div>
                  {permissionsAccess("action.report.generate.client_info") && (
                    <button
                      className="button-icontrigger caption"
                      onClick={() => dispatch(printClientInfo(client, activeInfoTab))}>
                      <span className="icon">
                        <PrintIcon />
                      </span>
                      <span>Печать анкеты</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="client-info-bottom">
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description nowrap">Документ</p>
                  <p className="client-info-bottom-item-value">{docName}</p>
                </div>
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description">Номер документа</p>
                  <p className="client-info-bottom-item-value nowrap">{client.document?.documentNumber}</p>
                </div>
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description">Идентификационный номер</p>
                  <p className="client-info-bottom-item-value nowrap">{client.personal}</p>
                </div>
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description">Номер дисконтной карты</p>
                  <p className="client-info-bottom-item-value nowrap">{client.discountCard}</p>
                </div>
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description">Телефон</p>
                  {client.contact && (
                    <p className="client-info-bottom-item-value">
                      <a className="link-dashed" href={`tel:${client.contact.phone}`}>
                        <span className='nowrap'>{client.contact.phone}</span>
                      </a>
                    </p>
                  )}
                </div>
                <div className="client-info-bottom-item">
                  <p className="client-info-bottom-item-description">
                    Подразделения, в которых обслуживался ранее
                  </p>
                  {cashBoxes.length > 0 && (
                    <div className="client-info-bottom-item-value">
                      <Dropdown className="dropdown-right dropdown-caret">
                        <DropdownTrigger className="link-dashed" href="#">
                          <span>{cashBoxNames}</span>
                          <span className="icon">
                            <CaretDownIcon />
                          </span>
                        </DropdownTrigger>
                        <DropdownContent>
                          <div className="dropdown-content caret">
                            <div className="dropdown-content-data">
                              {cashBoxes.map((cashBox) => {
                                let address = `${cashBox.city ? ` г. ${cashBox.city},` : ''} ул. ${
                                  cashBox.street
                                }, д. ${cashBox.house},
                                ${
                                  cashBox.building
                                    ? 'к. ' + cashBox.building + ','
                                    : ''
                                } каб. ${cashBox.office}`;
                                return (
                                  <p key={cashBox.id}>
                                    <strong>{cashBox.name}</strong>
                                    {address}
                                  </p>
                                );
                              })}
                            </div>
                          </div>
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  )}
                </div>
                {client.rejectionReason && !client.active && (
                  <div className="client-info-bottom-item">
                    <p className="client-info-bottom-item-description">
                      Основание для запрета обслуживания
                    </p>
                    <p className="client-info-bottom-item-value">{client.rejectionReason}</p>
                  </div>
                )}
                <div className="client-info-bottom-item">
                {client.kbStatus && (
                  <InfoItem
                    description="Статус"
                    value={
                      <span className={`label ${status?.label || kbStatus.UNKNOWN.label}`}>
                        {status?.text || kbStatus.UNKNOWN.text}
                      </span>
                    }
                  />
                )}

                {client.kbMessage && (
                  <InfoItem
                    description="Описание"
                    value={
                      <Dropdown className="dropdown-right dropdown-caret">
                        <DropdownTrigger className="link-dashed" href="#">
                          <span className="dropdown-title">
                            {client.kbMessage}
                          </span>
                          <span className="icon">
                            <CaretDownIcon />
                          </span>
                        </DropdownTrigger>
                        <DropdownContent>
                          <div className="dropdown-content caret">
                            <div className="dropdown-content-data">
                              {client.kbMessage}
                            </div>
                          </div>
                        </DropdownContent>
                      </Dropdown>
                    }
                    className="m-t-spacer"
                  />
                )}
              </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ClientShortInfo;
